import React, { useRef, useState, useEffect, useCallback } from "react";
import "./BatchUpload.css";
import { Icon, addToast } from "@msi/cobalt-react";
import ApiService from "../../services/apiService";
import { useOktaAuth } from "@okta/okta-react";

interface BatchUploadProps {
  onFileChange: (files: FileList | null) => void;
  onClose?: () => void;
  onMinimize?: () => void;
}

const FileItem: React.FC<{
  file: File;
  index: number;
  onRemoveFile: (index: number) => void;
}> = ({ file, index, onRemoveFile }) => {
  const formatFileSize = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  return (
    <div key={index} className="batch-upload-file-item">
      <div className="batch-upload-file-column batch-upload-file-icon">
        <Icon name="ic_file" size={24} />
      </div>
      <div className="batch-upload-file-column batch-upload-file-name">
        <span>{file.name}</span>
      </div>
      <div className="batch-upload-file-column batch-upload-file-size">
        <span>{formatFileSize(file.size)}</span>
      </div>
      <div className="batch-upload-file-column batch-upload-file-verification-icon">
        <button
          className="batch-upload-remove-button"
          onClick={() => onRemoveFile(index)}
        >
          <Icon name="ic_remove" size={24} />
        </button>
      </div>
    </div>
  );
};

const FileList: React.FC<{
  files: File[];
  onRemoveFile: (index: number) => void;
}> = ({ files, onRemoveFile }) => (
  <div className="batch-upload-file-list">
    {files.map((file, index) => (
      <FileItem
        key={index}
        file={file}
        index={index}
        onRemoveFile={onRemoveFile}
      />
    ))}
  </div>
);

const EmptyState: React.FC = () => (
  <div className="batch-upload-empty-state">
    <div className="batch-upload-upload-icon">
      <Icon
        name="ic_file_upload"
        size={75}
        color="primary"
        className="batch-upload-file-verification-icon"
      />
    </div>
    <div className="batch-upload-copy-content">
      <div className="batch-upload-copy">
        <h4 className="batch-upload-heading">Drag and Drop .csv file</h4>
        <p className="batch-upload-body-copy">Maximum 20MB file size</p>
      </div>
    </div>
  </div>
);

const Footer: React.FC<{
  isFileSelected: boolean;
  isLoading: boolean;
  validationErrors: string[];
  onSelectFile: () => void;
  onSubmit: () => void;
  files: File[];
}> = ({
  isFileSelected,
  isLoading,
  validationErrors,
  onSelectFile,
  onSubmit,
  files,
}) => (
    <div className="batch-upload-footer">
      <div className="batch-upload-list-content">
        <div className="batch-upload-button-group">
          <button
            className="batch-upload-outlined-button"
            onClick={onSelectFile}
          >
            <span>Select file</span>
          </button>
          <button
            className={`batch-upload-primary-button ${!isFileSelected ? "batch-upload-disabled" : ""
              }`}
            disabled={!isFileSelected || isLoading}
            onClick={onSubmit}
          >
            <span>
              {files.every((_, index) => validationErrors.length === 0)
                ? "Add API"
                : "Upload"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );

const BatchUpload: React.FC<BatchUploadProps> = ({
  onFileChange,
  onClose,
  onMinimize,
}) => {
  const dropAreaRef = useRef<HTMLDivElement>(null);
  const [isFileSelected, setIsFileSelected] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { authState } = useOktaAuth();
  const apiService = new ApiService();
  const [isMinimized, setIsMinimized] = useState(false);
  const TemplateFile = "/templates/batch_api_example.csv";
  const guideFile = "/guides/Guide%20to%20Add%20API%20Record%20through%20batch%20CSV.pdf";

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dropAreaRef.current?.classList.add("drag-over");
  };

  const handleDragLeave = () => {
    dropAreaRef.current?.classList.remove("drag-over");
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dropAreaRef.current?.classList.remove("drag-over");
    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles.length > 0) {
      updateFileSelection(droppedFiles);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 0) {
      updateFileSelection(selectedFiles);
    }
  };

  const updateFileSelection = (selectedFiles: File[]) => {
    setFiles(selectedFiles);
    setIsFileSelected(true);

    const dataTransfer = new DataTransfer();
    selectedFiles.forEach((file) => dataTransfer.items.add(file));
    onFileChange(dataTransfer.files);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    updateFileSelection(updatedFiles);

    setValidationErrors([]);

    if (updatedFiles.length === 0) {
      setIsFileSelected(false);
      onFileChange(null);
      const fileInput = document.getElementById("fileInput") as HTMLInputElement;
      if (fileInput) fileInput.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!files.length) return;
    setIsLoading(true);
    const formData = new FormData();
    files.forEach((file) => formData.append("file", file));

    try {
      const serviceReturn = await apiService.addAPIViaBatch(formData, authState);
      if (serviceReturn?.httpResponse?.status === 200) {
        addToast("success", "API added successfully.", { containerId: "default" });
        setFiles([]);
        setIsFileSelected(false);
        onFileChange(null);
      } else if (serviceReturn?.httpResponse?.status === 207) {
        const data = serviceReturn?.httpResponse.data as {
          message: string;
          errors: string[];
        };
        setValidationErrors([data.message, ...data.errors]);
        addToast("error", "Validation errors occurred", { containerId: "default" });
      } else {
        setValidationErrors(
          serviceReturn?.errorResponse || ["An unknown error occurred."]
        );
        addToast("error", "Failed to upload API batch", { containerId: "default" });
      }
    } catch (error: any) {
      setValidationErrors([error.message || "An unknown error occurred."]);
      addToast("error", "Failed to upload API batch", { containerId: "default" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectFile = () => {
    document.getElementById("fileInput")?.click();
  };

  const handleMinimizeClick = () => {
    setIsMinimized(true);
  };

  const handleMaximizeClick = () => {
    setIsMinimized(false);
  };

  const handleHeaderClick = () => {
    if (isMinimized) {
      handleMaximizeClick();
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setIsMinimized(true);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function formatError(error: string): string[] {
    if (error.includes("Row ")) {
      let msg = error.replace(/^Validation errors in CSV file\s*-?\s*/i, "");
      const rowRegex = /Row (\d+):/g;
      let result: string[] = [];
      let match: RegExpExecArray | null;
      let lastIndex = 0;
      let lastRow = null;
      let rowMatches: { row: string; index: number }[] = [];
      while ((match = rowRegex.exec(msg)) !== null) {
        rowMatches.push({ row: match[1], index: match.index });
        if (lastRow !== null) {
          const errorText = msg.substring(lastIndex, match.index).trim();
          if (errorText) result.push(errorText);
        }
        lastRow = match[1];
        lastIndex = match.index + match[0].length;
      }
      if (lastRow !== null) {
        const errorText = msg.substring(lastIndex).trim();
        if (errorText) result.push(errorText);
      }
      return rowMatches.map((rowMatch, i) => {
        const desc = result[i] || "";
        return `Line ${rowMatch.row}: ${desc}`;
      });
    }
    return [error];
  }

  return (
    <div
      className={`batch-upload-file-uploader ${isMinimized ? "batch-upload-minimized" : ""
        }`}
    >
      <div className="batch-upload-file-uploader-container">
        <div
          className="batch-upload-modal-header"
          onClick={handleHeaderClick}
        >
          <div className="batch-upload-title">
            <span>Upload file</span>
          </div>
          <div className="batch-upload-header-actions">
            {!isMinimized && onMinimize && (
              <button
                onClick={handleMinimizeClick}
                className="batch-upload-icon-button batch-upload-minimize"
              >
                <div className="batch-upload-icon"></div>
              </button>
            )}
            {isMinimized && (
              <button
                onClick={handleMaximizeClick}
                className="batch-upload-icon-button batch-upload-maximize"
              >
                <div className="batch-upload-icon"></div>
              </button>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="batch-upload-icon-button batch-upload-close"
              >
                <div className="batch-upload-icon"></div>
              </button>
            )}
          </div>
        </div>

        {!isMinimized && (
          <>
            <div className="batch-upload-content">
              <div className="batch-upload-title-area-1">
                <div className="batch-upload-left-side">
                  <span className="batch-upload-description">
                    For easy uploading, please check out the API record set-up
                    Guide.
                  </span>
                </div>
                <button className="batch-upload-tertiary-button">
                  <a style={{ textDecoration: 'none' }} href={guideFile} download>
                    <span>Read the Guide</span>
                  </a>
                </button>
              </div>
              <div className="batch-upload-title-area-2">
                <span className="batch-upload-description-text">
                  You can also download template csv files with example records:
                </span>
                <span className="batch-upload-description-text">
                  {" "}
                  <a href={TemplateFile} download> Template File</a> for API
                </span>
              </div>
            </div>

            <div
              className={`batch-upload-drop-area batch-upload-step-area ${isFileSelected ? "has-files" : ""
                }`}
              ref={dropAreaRef}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="fileInput"
                onChange={handleFileInputChange}
                accept=".csv"
                hidden
              />
              {isFileSelected ? <FileList files={files} onRemoveFile={handleRemoveFile} /> : <EmptyState />}
              {validationErrors.length > 0 && (
                <div className="batch-upload-error-messages">
                  <div className="primitive-callout">
                    <div className="callout-body-icon">
                      <div className="callout-content-function">
                        <div className="callout-content">
                          <div className="callout-title">
                            {
                              (() => {
                                const allFormatted = validationErrors.flatMap(formatError);
                                return `Found ${allFormatted.length} errors`;
                              })()
                            }
                          </div>
                          <div className="callout-body" id="validation-errors">
                            {validationErrors.map((error, index) =>
                              formatError(error).map((formatted, i) => (
                                <div key={index + '-' + i} className="batch-upload-error-message">
                                  {formatted}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Footer
              isFileSelected={isFileSelected}
              isLoading={isLoading}
              validationErrors={validationErrors}
              onSelectFile={handleSelectFile}
              onSubmit={handleSubmit}
              files={files}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default BatchUpload;
